import { Head } from "@inertiajs/react";
import type * as React from "react";
import { useRef, useState } from "react";

import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

export default function ScreenshotDebugger() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [debug, setDebug] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!url.trim()) return;

    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setLoading(true);
    setError(null);
    setScreenshotUrl(null);

    // Create AbortController for timeout handling (35 seconds)
    const controller = new AbortController();
    abortControllerRef.current = controller;
    timeoutRef.current = setTimeout(() => {
      controller.abort();
    }, 45000);

    try {
      let processedUrl = url.trim();
      // If the URL does not start with a protocol, prepend https://
      if (!/^https?:\/\//i.test(processedUrl)) {
        processedUrl = "https://" + processedUrl;
      }

      const response = await fetch("/screenshot-debugger", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify({ url: processedUrl, debug: debug }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({
          error: `HTTP ${response.status}: ${response.statusText}`,
        }))) as { error?: string; error_class?: string; step?: string };
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = (await response.json()) as {
        success: boolean;
        screenshot_url: string;
      };

      setScreenshotUrl(data.screenshot_url);
      setUrl("");
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === "AbortError") {
          setError(
            "Request timed out after 45 seconds. The screenshot capture is taking too long. This may be due to bot protection or slow page loading.",
          );
        } else {
          setError(err.message);
        }
      } else {
        setError("An error occurred while capturing the screenshot");
      }
    } finally {
      // Always cleanup timeout and controller
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      abortControllerRef.current = null;
      setLoading(false);
    }
  };

  return (
    <>
      <Head title="Screenshot Debugger | Test Scrappy Screenshots" />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <PublicHeader />

        <div className="container mx-auto max-w-6xl px-4 py-8 md:py-12">
          <div className="flex flex-col items-center text-center">
            <h1 className="text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">Screenshot Debugger</h1>
            <p className="text-muted-foreground mt-4 max-w-2xl text-base md:text-lg">
              Test Scrappy screenshots for debugging bot protection bypasses (Cloudflare, reCAPTCHA, etc.)
            </p>
          </div>

          <div className="mt-8 md:mt-12">
            <form onSubmit={(e) => void handleSubmit(e)} noValidate>
              <div className="mx-auto max-w-2xl">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Input
                    type="url"
                    placeholder="https://example.com"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    autoFocus
                    disabled={loading}
                    className="h-12 text-base"
                    required
                  />
                  <Button
                    type="submit"
                    disabled={loading}
                    className="h-12 min-w-[140px] bg-orange-500 whitespace-nowrap hover:bg-orange-600"
                  >
                    {loading ? "Capturing..." : "Capture Screenshot"}
                  </Button>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Checkbox
                    id="debug-mode"
                    checked={debug}
                    onCheckedChange={(checked) => setDebug(checked === true)}
                    disabled={loading}
                  />
                  <label htmlFor="debug-mode" className="cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                    Debug mode (skip cache)
                  </label>
                </div>
              </div>
            </form>
          </div>

          {error && (
            <div className="mx-auto mt-8 max-w-4xl md:mt-12">
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/50 dark:text-red-300">
                <p className="font-semibold">Error</p>
                <p className="mt-1">{error}</p>
              </div>
            </div>
          )}

          {screenshotUrl && (
            <div className="mx-auto mt-8 max-w-5xl md:mt-12">
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Screenshot Result</h2>
                </div>
                <div className="relative overflow-hidden rounded-md border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-900">
                  <img
                    src={screenshotUrl}
                    alt="Screenshot"
                    className="h-auto w-full"
                    style={{ maxWidth: "100%", height: "auto" }}
                  />
                </div>
                <div className="mt-4 flex gap-2">
                  <Button
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = screenshotUrl;
                      link.download = `screenshot-${Date.now()}.jpg`;
                      link.click();
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Download Screenshot
                  </Button>
                </div>
              </div>
            </div>
          )}

          {!screenshotUrl && !error && (
            <div className="mx-auto mt-8 max-w-4xl md:mt-12">
              <div className="rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-800">
                <p className="text-gray-500 dark:text-gray-400">
                  Enter a URL above to capture a screenshot using Scrappy.
                </p>
                <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
                  Screenshots are captured at 1440x756 resolution and displayed directly in the browser.
                </p>
              </div>
            </div>
          )}
        </div>

        <SiteFooter />
      </div>
    </>
  );
}
